import React, { useState } from 'react';
import { Trash2, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Car } from 'lucide-react';
import { SimulatorCartItem, TimePeriod, BalneotherapyType, PERIOD_LABELS } from '../../types';
import { RevenueEvolution, convertDailyToDisplayQuantity } from '../../../../utils/simulatorCalculations';
import { generateNgapCodeLabel } from '../../../../utils/urlUtils';
import { getDisplacementValue } from '../../../../utils/pricingUtils';
import BalneotherapySelector from '../BalneotherapySelector';

interface SimulatorCartMobileCardProps {
  item: SimulatorCartItem;
  evolution?: RevenueEvolution;
  selectedPeriod: TimePeriod;
  onUpdateQuantity: (itemId: string, newQuantity: number) => void;
  onUpdateHomeVisitPercentage: (itemId: string, percentage: number) => void;
  onUpdateBalneotherapy: (itemId: string, balneotherapy: BalneotherapyType) => void;
  onRemoveFromCart: (itemId: string) => void;
  darkMode: boolean;
}

const SimulatorCartMobileCard: React.FC<SimulatorCartMobileCardProps> = ({
  item,
  evolution,
  selectedPeriod,
  onUpdateQuantity,
  onUpdateHomeVisitPercentage,
  onUpdateBalneotherapy,
  onRemoveFromCart,
  darkMode
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!evolution) return null;

  const displayQuantity = convertDailyToDisplayQuantity(item.quantity, selectedPeriod);

  const handleQuantityChange = (newDisplayQuantity: number) => {
    const divisor = selectedPeriod === 'day' ? 1 :
                    selectedPeriod === 'week' ? 5 :
                    selectedPeriod === 'month' ? 20 : 240;
    const dailyEquivalent = newDisplayQuantity / divisor;
    onUpdateQuantity(item.id, Math.max(0.1, dailyEquivalent));
  };

  const janv2026Value = evolution.janv2026?.value || evolution.current.value;
  const may2026Value = evolution.may2026?.value || janv2026Value;
  const sept2026Value = evolution.september2026?.value || may2026Value;
  const july2027Value = evolution.july2027?.value || sept2026Value;

  const diff2026 = sept2026Value - evolution.current.value;
  const diff2027 = july2027Value - evolution.current.value;

  const homeVisitExtra = item.homeVisitPercentage > 0
    ? getDisplacementValue(item.act.indemnite_deplacement) * (item.homeVisitPercentage / 100) * displayQuantity
    : 0;

  const trendColor = (value: number) =>
    value > 0 ? 'text-appines-emerald' : value < 0 ? 'text-[#c04040]' : darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65';

  const mutedText = darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65';
  const bodyText = darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65';
  const valueText = darkMode ? 'text-appines-white' : 'text-appines-black';
  const inputClass = `w-full px-2 py-1.5 text-sm text-center border rounded focus:ring-2 focus:ring-appines-emerald/25 focus:border-appines-emerald ${
    darkMode ? 'bg-appines-petrol border-appines-white/20 text-appines-white' : 'bg-appines-white border-[rgba(1,42,54,0.2)] text-appines-black'
  }`;

  return (
    <div className={`rounded-lg border p-3 space-y-3 ${
      darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'
    }`}>
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-xs font-semibold px-2 py-1 rounded ${
            darkMode ? 'bg-appines-emerald/15 text-appines-emerald' : 'bg-appines-glass/25 text-appines-petrol'
          }`}>
            {generateNgapCodeLabel(item.act, 'auto')}
          </span>
          <button
            onClick={() => onRemoveFromCart(item.id)}
            className={`shrink-0 p-1.5 rounded-full transition-colors ${
              darkMode
                ? 'text-appines-white/45 hover:text-appines-petal hover:bg-appines-petal/10'
                : 'text-appines-petrol/45 hover:text-[#c04040] hover:bg-appines-petal/10'
            }`}
            aria-label="Supprimer"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        <p className={`text-sm leading-snug ${darkMode ? 'text-appines-white/80' : 'text-appines-black'}`}>
          {item.act.libelle}
        </p>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-2 gap-2">
        <label className={`flex flex-col gap-1 text-xs ${mutedText}`}>
          Quantite / {PERIOD_LABELS[selectedPeriod].toLowerCase()}
          <input
            type="number" min="1" max="1000"
            value={displayQuantity}
            onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
            className={inputClass}
          />
        </label>

        <label className={`flex flex-col gap-1 text-xs ${mutedText}`}>
          % domicile
          <input
            type="number" min="0" max="100"
            value={item.homeVisitPercentage}
            onChange={(e) => onUpdateHomeVisitPercentage(item.id, parseInt(e.target.value) || 0)}
            className={inputClass}
          />
        </label>
      </div>

      {homeVisitExtra > 0 && (
        <div className="flex items-center gap-1 text-xs text-appines-emerald">
          <Car className="h-3 w-3" />
          +{homeVisitExtra.toFixed(2)}€ deplacement
        </div>
      )}

      {/* Revenus */}
      <div className="grid grid-cols-2 gap-2">
        <div className={`rounded-md p-2 ${darkMode ? 'bg-appines-white/5' : 'bg-[rgba(1,42,54,0.04)]'}`}>
          <div className={`text-[10px] uppercase tracking-wide font-medium ${mutedText}`}>Actuel</div>
          <div className={`text-base font-semibold ${valueText}`}>
            {evolution.current.value.toFixed(2)}€
          </div>
        </div>

        <div className={`rounded-md p-2 ${darkMode ? 'bg-appines-emerald/10' : 'bg-appines-glass/20'}`}>
          <div className={`text-[10px] uppercase tracking-wide font-medium ${darkMode ? 'text-appines-emerald' : 'text-appines-petrol'}`}>2026</div>
          <div className={`text-base font-semibold ${
            sept2026Value > evolution.current.value ? 'text-appines-emerald'
              : sept2026Value < evolution.current.value ? 'text-[#c04040]'
              : valueText
          }`}>
            {sept2026Value.toFixed(2)}€
          </div>
          {Math.abs(diff2026) > 0.01 && (
            <div className={`flex items-center gap-1 text-[11px] ${trendColor(diff2026)}`}>
              {diff2026 > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {diff2026 > 0 ? '+' : ''}{diff2026.toFixed(2)}€
            </div>
          )}
        </div>
      </div>

      {/* 2027 */}
      <div className={`flex items-center justify-between px-2 py-1.5 rounded-md border border-dashed ${
        darkMode ? 'border-appines-white/20' : 'border-[rgba(1,42,54,0.2)]'
      }`}>
        <span className={`text-xs font-medium ${mutedText}`}>Projection 2027</span>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-semibold ${
            july2027Value > sept2026Value ? 'text-appines-emerald'
              : july2027Value < sept2026Value ? 'text-[#c04040]'
              : bodyText
          }`}>
            {july2027Value.toFixed(2)}€
          </span>
          {Math.abs(diff2027) > 0.01 && (
            <span className={`text-[11px] ${trendColor(diff2027)}`}>
              ({diff2027 > 0 ? '+' : ''}{diff2027.toFixed(2)}€)
            </span>
          )}
        </div>
      </div>

      {/* Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-center gap-1 text-xs py-1.5 rounded transition-colors ${
          darkMode
            ? 'text-appines-white/45 hover:text-appines-white hover:bg-appines-white/10'
            : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'
        }`}
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? 'Masquer' : 'Plus de details'}
      </button>

      {expanded && (
        <div className={`space-y-3 pt-2 border-t ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.08)]'}`}>
          <div className="space-y-1">
            <div className={`text-xs font-medium ${mutedText}`}>Balneotherapie</div>
            <BalneotherapySelector
              value={item.balneotherapy}
              onChange={(balneotherapy) => onUpdateBalneotherapy(item.id, balneotherapy)}
              darkMode={darkMode}
              displayQuantity={displayQuantity}
            />
          </div>

          {evolution.original.value > 0 && (
            <div className="flex items-center justify-between text-xs">
              <span className={mutedText}>Tarif original</span>
              <span className={bodyText}>{evolution.original.value.toFixed(2)}€</span>
            </div>
          )}

          {evolution.janv2026 && (
            <div className="flex items-center justify-between text-xs">
              <span className={mutedText}>Janv. 2026</span>
              <span className={bodyText}>{evolution.janv2026.value.toFixed(2)}€</span>
            </div>
          )}
          {evolution.may2026 && (
            <div className="flex items-center justify-between text-xs">
              <span className={mutedText}>Mai 2026</span>
              <span className={bodyText}>{evolution.may2026.value.toFixed(2)}€</span>
            </div>
          )}
          {evolution.september2026 && (
            <div className="flex items-center justify-between text-xs">
              <span className={mutedText}>Sept. 2026</span>
              <span className={bodyText}>{evolution.september2026.value.toFixed(2)}€</span>
            </div>
          )}

          <div className="flex items-center justify-between text-xs">
            <span className={mutedText}>Indemnite</span>
            <span className={bodyText}>
              {item.act.indemnite_deplacement === 'IFS' ? 'IFS : 4€' : 'IFD : 2,5€'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimulatorCartMobileCard;
